import {Select} from '@wandb/weave/components/Form/Select';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useCallback, useEffect, useMemo} from 'react';
import {type GroupBase} from 'react-select';

import {Alias} from '../../../Alias';
import {type AliasOrVersionOption, type ArtifactOption} from '../util';
import * as S from './ArtifactPathSelector.styles';

interface AliasOrVersionSelectorProps {
  selectedArtifact?: ArtifactOption | null;
  value?: AliasOrVersionOption | null;
  onVersionSelect?: (alias: AliasOrVersionOption | null) => void;
}

interface GroupedAliasOptions extends GroupBase<AliasOrVersionOption> {
  label: string;
  options: AliasOrVersionOption[];
}

export const AliasOrVersionSelector = ({
  selectedArtifact,
  value,
  onVersionSelect,
}: AliasOrVersionSelectorProps) => {
  const groupedOptions = useMemo(
    () => selectedArtifact?.aliasesOrVersions ?? [],
    [selectedArtifact?.aliasesOrVersions]
  );

  // auto-select "latest" when a new artifact is selected
  useEffect(() => {
    if (selectedArtifact && !value && onVersionSelect) {
      const latestAlias = groupedOptions
        .flatMap(group => group.options)
        .find(option => option.alias.toLowerCase() === 'latest');

      if (latestAlias) {
        onVersionSelect(latestAlias);
      }
    }
  }, [selectedArtifact, value, groupedOptions, onVersionSelect]);

  const handleChange = useCallback(
    (option: AliasOrVersionOption | null) => {
      if (onVersionSelect) {
        onVersionSelect(option);
      }
    },
    [onVersionSelect]
  );

  const isDisabled = !selectedArtifact;
  const select = (
    <S.DisabledDropdownContainer>
      <Select<AliasOrVersionOption, false, GroupedAliasOptions>
        value={value}
        options={groupedOptions}
        placeholder="Select version or alias"
        getOptionValue={option => option.value}
        getOptionLabel={option => option.label}
        formatOptionLabel={AliasOrVersionOptionLabel}
        onChange={handleChange}
        isDisabled={isDisabled}
        isSearchable
        isClearable={true}
      />
    </S.DisabledDropdownContainer>
  );

  if (isDisabled) {
    return (
      <Tooltip
        side="top"
        align="start"
        trigger={select}
        content="Select an artifact first"
      />
    );
  }

  return select;
};

const AliasOrVersionOptionLabel = (option: AliasOrVersionOption) => {
  if (option.type === 'alias') {
    return <Alias alias={{alias: option.alias}} />;
  }

  return <span>{option.alias}</span>;
};
